import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ExportExcelButtonComponent } from 'src/app/common/components/export-excel-button/export-excel-button.component';
import { AssessRequestRaService } from 'src/app/assess-request-ra/assess-request-ra.service';
import { environment } from 'src/environments/environment';
import { VIEW_GROUP_SUBSTANCE_COLUMNS } from 'src/app/common/models/tableColumns';

@Component({
  selector: 'app-view-group',
  templateUrl: './view-group.component.html',
  styleUrls: ['./view-group.component.scss']
})
export class ViewGroupComponent implements OnInit {
  @ViewChild('viewGroupExportRef') viewGroupExportRef!: ExportExcelButtonComponent;
  groupId = '';
  groupName = '';
  groupType = '';
  regulatoryGroup = '';
  division = '';
  substanceTableData: any[] = [];
  substanceTableColumns: any[] = [];
  exportColumns: any[] = [];
  loading = false;
  viewBackContext: any = {};
  notifySersDialog = false;
  notifySersDetails: any[] = [];
  notifySersPureCodes: string[] = [];
  readonly notifyIssueCategoryList = [
    { name: 'CAS#', value: 'CAS#' },
    { name: 'Parent CAS#', value: 'Parent CAS#' },
    { name: 'E#', value: 'E#' },
    { name: 'INS#', value: 'INS#' },
    { name: 'EINECS#', value: 'EINECS#' },
    { name: 'INCI', value: 'INCI' },
    { name: 'Synonym', value: 'Synonym' },
    { name: 'CF details', value: 'CF details' }
  ];
  backbuttonflag = true;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private matSnackBar: MatSnackBar,
    private assessRequestRa: AssessRequestRaService
  ) { }

  ngOnInit(): void {
    this.initializeTableColumns();
    this.initializeViewBackContext();
    const hasStateTableData = this.loadTableDataFromNavigationState();

    this.route.params.subscribe(params => {
      this.groupId = params['id'];
    });

    this.route.queryParams.subscribe(params => {
      this.groupName = params['groupName'] || this.groupName || '';
      this.groupType = params['groupType'] || this.groupType || 'Regulatory Group';
      this.division = params['division'] || this.division || '';
      this.regulatoryGroup = 'Regulatory Group';
      const rawBackFlag = params['backbuttonflag'];
      this.backbuttonflag = rawBackFlag === undefined ? true : String(rawBackFlag).trim().toLowerCase() === 'true';
      if (!hasStateTableData) {
        this.loadGroupDetailsFromApi();
      }
    });
  }

  private loadTableDataFromNavigationState(): boolean {
    const navigation = this.router.getCurrentNavigation();
    const navState = navigation && navigation.extras && navigation.extras.state
      ? navigation.extras.state
      : null;
    const historyState = window.history && window.history.state
      ? window.history.state
      : null;

    const stateData = this.getSubstanceRowsFromState(navState);
    const historyStateData = this.getSubstanceRowsFromState(historyState);

    if (Array.isArray(stateData) && stateData.length > 0) {
      this.substanceTableData = stateData.map((row: any) => this.mapViewGroupRow(row));
      this.updateDynamicIdentifierColumnWidths();
      return true;
    } else if (Array.isArray(historyStateData) && historyStateData.length > 0) {
      this.substanceTableData = historyStateData.map((row: any) => this.mapViewGroupRow(row));
      this.updateDynamicIdentifierColumnWidths();
      return true;
    }

    return false;
  }

  private initializeViewBackContext(): void {
    const navigation = this.router.getCurrentNavigation();
    const navState = navigation && navigation.extras && navigation.extras.state
      ? navigation.extras.state
      : null;
    const historyState = window.history && window.history.state
      ? window.history.state
      : null;

    const navContext = navState && navState.viewBackContext ? navState.viewBackContext : null;
    const historyContext = historyState && historyState.viewBackContext ? historyState.viewBackContext : null;
    this.viewBackContext = navContext || historyContext || {};
  }

  private getSubstanceRowsFromState(state: any): any[] {
    if (!state) {
      return [];
    }

    if (Array.isArray(state.substanceTableData)) {
      return state.substanceTableData;
    }

    if (state.editGroupContext && Array.isArray(state.editGroupContext.substanceTableData)) {
      return state.editGroupContext.substanceTableData;
    }

    return [];
  }

  loadGroupDetailsFromApi(): void {
    if (!this.groupName || !this.groupType || !this.division) {
      return;
    }

    this.loading = true;
    const primaryApiUrl = this.buildViewGroupApiUrl(this.groupName, this.groupType, this.division);
    this.assessRequestRa.getData(primaryApiUrl).subscribe((response: any) => {
      const apiRows = this.extractRowsFromViewGroupResponse(response);
      if (apiRows.length > 0) {
        this.substanceTableData = apiRows.map((row: any) => this.mapViewGroupRow(row));
        this.updateDynamicIdentifierColumnWidths();
        this.loading = false;
        return;
      }

      // Fallback: some services expect groupType without spaces.
      const compactGroupType = (this.groupType || '').replace(/\s+/g, '');
      if (compactGroupType && compactGroupType !== this.groupType) {
        const fallbackApiUrl = this.buildViewGroupApiUrl(this.groupName, compactGroupType, this.division);
        this.assessRequestRa.getData(fallbackApiUrl).subscribe((fallbackResponse: any) => {
          const fallbackRows = this.extractRowsFromViewGroupResponse(fallbackResponse);
          if (fallbackRows.length > 0) {
            this.substanceTableData = fallbackRows.map((row: any) => this.mapViewGroupRow(row));
            this.updateDynamicIdentifierColumnWidths();
          }
          this.loading = false;
        }, () => {
          this.loading = false;
          if (!this.substanceTableData || this.substanceTableData.length === 0) {
            this.substanceTableData = [];
            this.updateDynamicIdentifierColumnWidths();
          }
          this.matSnackBar.open('Unable to fetch view group details', 'close', {
            duration: 3000,
            panelClass: ['error']
          });
        });
        return;
      }

      if (!this.substanceTableData || this.substanceTableData.length === 0) {
        this.substanceTableData = [];
        this.updateDynamicIdentifierColumnWidths();
      }
      this.loading = false;
    }, () => {
      this.loading = false;
      if (!this.substanceTableData || this.substanceTableData.length === 0) {
        this.substanceTableData = [];
        this.updateDynamicIdentifierColumnWidths();
      }
      this.matSnackBar.open('Unable to fetch view group details', 'close', {
        duration: 3000,
        panelClass: ['error']
      });
    });
  }

  private buildViewGroupApiUrl(groupName: string, groupType: string, division: string): string {
    const encodedGroupName = encodeURIComponent(groupName || '');
    const encodedGroupType = encodeURIComponent(groupType || '');
    const encodedDivision = encodeURIComponent(division || '');
    return environment.APIFAsTRule + '/rules/substancegroups/viewgroup/' + encodedGroupName + '/' + encodedGroupType + '/' + encodedDivision;
  }

  extractRowsFromViewGroupResponse(response: any): any[] {
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      const flattenedRows: any[] = [];
      const preferredKeys = ['substanceData', 'substances', 'data', 'items', 'results', 'result', 'payload', 'response'];

      for (const item of response) {
        if (!item) {
          continue;
        }

        let nestedFound = false;
        for (const key of preferredKeys) {
          if (Array.isArray(item[key])) {
            flattenedRows.push(...item[key]);
            nestedFound = true;
            break;
          }
        }

        if (!nestedFound) {
          flattenedRows.push(item);
        }
      }

      if (flattenedRows.length > 0) {
        return flattenedRows;
      }

      return response;
    }

    const topLevelPreferredKeys = ['substanceData', 'substances', 'data', 'items', 'results', 'result', 'payload', 'response'];
    for (const key of topLevelPreferredKeys) {
      if (Array.isArray(response[key])) {
        return response[key];
      }

      if (response[key] && typeof response[key] === 'object') {
        const nestedRows = this.extractRowsFromViewGroupResponse(response[key]);
        if (nestedRows.length > 0) {
          return nestedRows;
        }
      }
    }

    // Fallback: some APIs wrap list in an unknown key.
    const keys = Object.keys(response);
    for (const key of keys) {
      if (Array.isArray(response[key])) {
        return response[key];
      }
    }

    return [];
  }

  mapViewGroupRow(row: any): any {
    const source = row && row.substance ? row.substance : row;
    const valueOf = (obj: any, keys: string[]) => this.getValueFromAnyKey(obj, keys);

    const cfDataFromDetails = Array.isArray(source && source.cfDetails)
      ? source.cfDetails
        .map((x: any) => x && (x.cfDisplayText || x.cf || x.component || ''))
        .filter((x: any) => x)
        .join(', ')
      : '';

    return {
      pureCode: valueOf(source, ['pureCode', 'purecode', 'PURE Code', 'Pure Code']),
      sirId: valueOf(source, ['sirId', 'substanceId', 'substanceID', 'SIR ID']),
      inciName: valueOf(source, ['inciName', 'INCI Name', 'inci']),
      cas: valueOf(source, ['cas', 'CAS#', 'CAS #']),
      parentCAS: valueOf(source, ['parentCAS', 'parentCas', 'Parent CAS#', 'Parent CAS']),
      ins: valueOf(source, ['ins', 'INS#', 'INS']),
      einecs: valueOf(source, ['einecs', 'EINECS']),
      synonyms: valueOf(source, ['synonyms', 'substanceName', 'Substance Name']),
      cfInput: valueOf(source, ['cfInput', 'CF Input', 'cfinputdata', 'CFInputData']),
      cfData: valueOf(source, ['cfData', 'CF Data', 'cfdatafromsir', 'CFDataFromSIR']) || cfDataFromDetails,
      notifySers: this.toSwitchValue(valueOf(source, ['notifySers', 'notifygpc', 'notifyGpc'])),
      notifySir: this.toDisplaySwitchValue(valueOf(source, ['notifySir', 'notifysir', 'notifySirFlag']))
    };
  }

  private getValueFromAnyKey(obj: any, keys: string[]): any {
    if (!obj) {
      return '';
    }

    for (const key of keys) {
      if (obj[key] !== undefined && obj[key] !== null) {
        return obj[key];
      }
    }

    const normalizedMap: any = {};
    Object.keys(obj).forEach((actualKey: string) => {
      normalizedMap[this.normalizeKey(actualKey)] = obj[actualKey];
    });

    for (const key of keys) {
      const normalized = this.normalizeKey(key);
      if (normalizedMap[normalized] !== undefined && normalizedMap[normalized] !== null) {
        return normalizedMap[normalized];
      }
    }

    return '';
  }

  private normalizeKey(key: string): string {
    return String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private toSwitchValue(value: any): any {
    if (value === true || value === false) {
      return value;
    }
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'y' || normalized === 'yes' || normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'n' || normalized === 'no' || normalized === 'false' || normalized === '0') {
      return false;
    }
    return value;
  }

  private toDisplaySwitchValue(value: any): string {
    if (value === true) {
      return 'Y';
    }
    if (value === false) {
      return '';
    }

    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'y' || normalized === 'yes' || normalized === 'true' || normalized === '1') {
      return 'Y';
    }
    if (normalized === 'n' || normalized === 'no' || normalized === 'false' || normalized === '0') {
      return '';
    }

    return String(value || '');
  }

  initializeTableColumns(): void {
    this.substanceTableColumns = VIEW_GROUP_SUBSTANCE_COLUMNS.map((column: any) => ({ ...column }));
    this.updateDynamicIdentifierColumnWidths();
    this.exportColumns = this.substanceTableColumns.map((column: any) => ({
      field: column.field,
      header: column.header
    }));
  }

  private updateDynamicIdentifierColumnWidths(): void {
    const pureCodeWidth = this.calculateTextBasedColumnWidth('pureCode', 140, 300, 0.9);
    const sirIdWidth = this.calculateTextBasedColumnWidth('sirId', 120, 190, 1);

    this.substanceTableColumns = this.substanceTableColumns.map((column: any) => {
      if (column.field === 'pureCode') {
        return { ...column, width: pureCodeWidth };
      }
      if (column.field === 'sirId') {
        return { ...column, width: sirIdWidth };
      }
      return column;
    });
  }

  private calculateTextBasedColumnWidth(field: string, minWidth: number, maxWidth: number, percentile: number): string {
    const headerLength = this.getHeaderLengthForField(field);
    const dataLengths = (this.substanceTableData || [])
      .map((row: any) => row && row[field] !== undefined && row[field] !== null ? String(row[field]).length : 0)
      .sort((a: number, b: number) => a - b);

    const percentileLength = this.getPercentileLength(dataLengths, percentile);
    const maxTextLength = Math.max(headerLength, percentileLength);
    const computedWidth = (maxTextLength * 6) + 24;
    const clampedWidth = Math.min(maxWidth, Math.max(minWidth, computedWidth));
    return `${clampedWidth}px`;
  }

  private getPercentileLength(sortedLengths: number[], percentile: number): number {
    if (!sortedLengths || sortedLengths.length === 0) {
      return 0;
    }
    const safePercentile = Math.min(1, Math.max(0, percentile));
    const index = Math.floor((sortedLengths.length - 1) * safePercentile);
    return sortedLengths[index];
  }

  private getHeaderLengthForField(field: string): number {
    const column = VIEW_GROUP_SUBSTANCE_COLUMNS.find((item: any) => item.field === field);
    return column && column.header ? String(column.header).length : 0;
  }

  onEditGroup(): void {
    const divisionForBack = this.resolveBackDivision();
    this.router.navigate(['/administration/regulatory-group-master'], {
      state: {
        viewBackContext: {
          ...this.viewBackContext,
          division: divisionForBack
        },
        editGroupContext: {
          groupId: this.groupId,
          groupName: this.groupName,
          groupType: this.groupType || 'Regulatory Group',
          division: divisionForBack,
          substanceTableData: this.substanceTableData
        }
      }
    });
  }

  getSelectedNotifySersCount(): number {
    return (this.substanceTableData || []).filter((row: any) => !!row.notifySers).length;
  }

  onNotifySersClicked(): void {
    const selectedRows = (this.substanceTableData || []).filter((row: any) => !!row.notifySers);
    if (selectedRows.length === 0) {
      this.matSnackBar.open('Select the Pure Codes', 'close', {
        duration: 3000,
        panelClass: ['error']
      });
      return;
    }

    this.notifySersDetails = selectedRows.map((row: any) => ({
      purecode: row.pureCode || '',
      sirId: row.sirId || '',
      pureDescription: row.synonyms || '',
      inciName: row.inciName || '',
      issueCategoryList: this.notifyIssueCategoryList,
      selectedIssueCategory: [],
      commentsToUser: ''
    }));
    this.notifySersPureCodes = this.notifySersDetails
      .map((row: any) => row.purecode)
      .filter((value: string) => !!value);
    this.notifySersDialog = true;
  }

  sendMailtoSers(): void {
    if (!this.notifySersDetails || this.notifySersDetails.length === 0) {
      this.notifySersDialog = false;
      return;
    }

    let emailData = '';
    this.notifySersDetails.forEach((element: any) => {
      const selectedIssueCategory = Array.isArray(element.selectedIssueCategory)
        ? element.selectedIssueCategory.toString()
        : '';
      emailData = emailData + '\r\nPure Code: ' + element.purecode
        + '\r\n[SIR ID : ' + element.sirId + ']'
        + '\r\nBackbone Identity : ' + selectedIssueCategory
        + '\r\nComments : ' + (element.commentsToUser || '')
        + '\r\nPure Description : ' + (element.pureDescription || '') + '\r\n';
    });

    const data = {
      NotificationGroup: 'GPC',
      Subject: null,
      EmailBody: emailData,
      ToEmailList: null,
      FromEmail: null,
      CCEmailList: null,
      EmailType: 'html',
      UPD: this.division || '',
      PureCodes: this.notifySersPureCodes.toString(),
      RequestID: 0
    };
    const apiurl = environment.APIFAsT + '/email';
    this.notifySersDialog = false;
    this.assessRequestRa.postData(apiurl, data).subscribe((response: any) => {
      if (response && response.message !== null) {
        this.matSnackBar.open('Email message sent to SERS team successfully', 'close', {
          duration: 5000,
          panelClass: ['success']
        });
      }
    }, () => {
      this.matSnackBar.open('Unable to send email to SERS team', 'close', {
        duration: 3000,
        panelClass: ['error']
      });
    });
  }

  onExport(): void {
    if (!this.substanceTableData || this.substanceTableData.length === 0) {
      this.matSnackBar.open('No records available to export', 'close', {
        duration: 3000,
        panelClass: ['error']
      });
      return;
    }

    if (this.viewGroupExportRef) {
      this.viewGroupExportRef.exportToExcel();
    }
  }

  onBack(): void {
    const divisionForBack = this.resolveBackDivision();
    const backState = {
      viewBackToList: true,
      viewBackContext: {
        ...this.viewBackContext,
        division: divisionForBack
      }
    };

    this.router.navigate(['/administration/regulatory-group-master'], {
      state: {
        ...backState
      }
    }).then((navigated) => {
      if (!navigated || this.router.url.indexOf('/administration/regulatory-group-master') === -1) {
        this.router.navigate(['/administration/regulatory-group-master'], {
          state: {
            ...backState
          },
          replaceUrl: true
        });
      }
    });
  }

  private resolveBackDivision(): string {
    const queryDivision = this.route && this.route.snapshot && this.route.snapshot.queryParams
      ? this.route.snapshot.queryParams['division']
      : '';
    const historyStateDivision = window.history && window.history.state && window.history.state.viewBackContext
      ? window.history.state.viewBackContext.division
      : '';

    const stateDivision = this.viewBackContext && this.viewBackContext.division
      ? this.viewBackContext.division
      : '';

    return String(this.division || queryDivision || stateDivision || historyStateDivision || '').trim();
  }
}
